import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { CampaignDetail, Campaign, Referral, CreateRulePayload } from '@/lib/referral-types'
import {
  formatCents, formatTs, campaignStatusVariant, referralStatusVariant,
  TRIGGER_EVENTS, REFERRER_REWARD_TYPES, REFERRED_REWARD_TYPES,
} from '@/lib/referral-utils'
import { Card, CardContent } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Tabs } from '@/components/ui/Tabs'
import { Modal } from '@/components/ui/Modal'
import { PiiField } from '@/components/ui/PiiField'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { usePresentation } from '@/components/layout/AppShell'

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>()
  const queryClient = useQueryClient()
  const { presenting } = usePresentation()
  const [activeTab, setActiveTab] = useState('details')

  // Edit campaign modal
  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState<Partial<Campaign>>({})
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Add rule modal
  const [ruleOpen, setRuleOpen] = useState(false)
  const [ruleForm, setRuleForm] = useState<CreateRulePayload>({
    trigger_event: 'paid_conversion',
    referrer_reward_type: 'cash_flat',
    referrer_reward_value: 0,
    referrer_reward_recurring: 0,
    referred_reward_type: null,
    referred_reward_value: null,
  })
  const [ruleSaving, setRuleSaving] = useState(false)
  const [ruleError, setRuleError] = useState<string | null>(null)

  // Delete rule
  const [deleting, setDeleting] = useState<string | null>(null)

  // Create code
  const [codeEmail, setCodeEmail] = useState('')
  const [codeCustom, setCodeCustom] = useState('')
  const [codeSaving, setCodeSaving] = useState(false)
  const [codeError, setCodeError] = useState<string | null>(null)
  const [codeResult, setCodeResult] = useState<{ code: string; referral_url: string; user_name: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referral-campaign', campaignId],
    queryFn: () => authApi.get<CampaignDetail>(`/api/v1/admin/referral/campaigns/${campaignId}`),
    enabled: !!campaignId,
  })

  // Referrals for this campaign
  const { data: referralsData, isLoading: referralsLoading } = useQuery({
    queryKey: ['admin-referrals', { campaign_id: campaignId }],
    queryFn: () =>
      authApi.get<{ referrals: Referral[] }>('/api/v1/admin/referral/referrals', {
        params: { campaign_id: campaignId, limit: 100 },
      }),
    enabled: !!campaignId && activeTab === 'referrals',
  })

  // Codes for this campaign
  interface CampaignCode {
    id: string
    code: string
    user_id: string
    user_name: string | null
    user_email: string
    referral_count: number
    is_active: number
    created_at: number
  }
  const { data: codesData, isLoading: codesLoading } = useQuery({
    queryKey: ['admin-referral-codes', campaignId],
    queryFn: () =>
      authApi.get<{ codes: CampaignCode[] }>(`/api/v1/admin/referral/campaigns/${campaignId}/codes`),
    enabled: !!campaignId && activeTab === 'codes',
  })

  if (isLoading) {
    return <div className="flex items-center justify-center py-24"><LoadingSpinner size="lg" /></div>
  }

  const campaign = data?.campaign
  const rules = data?.rules ?? []
  const stats = data?.stats

  if (!campaign) {
    return <p className="text-[var(--content-tertiary)] py-12 text-center">Campaign not found.</p>
  }

  function openEditModal() {
    setEditForm({
      name: campaign!.name,
      description: campaign!.description,
      status: campaign!.status,
      referrer_type: campaign!.referrer_type,
      referred_product: campaign!.referred_product,
      attribution_window_days: campaign!.attribution_window_days,
      max_referrals_per_user: campaign!.max_referrals_per_user,
      max_total_referrals: campaign!.max_total_referrals,
    })
    setEditError(null)
    setEditOpen(true)
  }

  async function handleSaveEdit() {
    setEditSaving(true)
    setEditError(null)
    try {
      await authApi.put(`/api/v1/admin/referral/campaigns/${campaignId}`, editForm)
      queryClient.invalidateQueries({ queryKey: ['admin-referral-campaign', campaignId] })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-campaigns'] })
      setEditOpen(false)
    } catch (e) {
      setEditError(String(e))
    } finally {
      setEditSaving(false)
    }
  }

  async function handleAddRule() {
    setRuleSaving(true)
    setRuleError(null)
    try {
      await authApi.post(`/api/v1/admin/referral/campaigns/${campaignId}/rules`, {
        ...ruleForm,
        referred_reward_type: ruleForm.referred_reward_type || null,
        referred_reward_value: ruleForm.referred_reward_value || null,
      })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-campaign', campaignId] })
      setRuleOpen(false)
      setRuleForm({
        trigger_event: 'paid_conversion',
        referrer_reward_type: 'cash_flat',
        referrer_reward_value: 0,
        referrer_reward_recurring: 0,
        referred_reward_type: null,
        referred_reward_value: null,
      })
    } catch (e) {
      setRuleError(String(e))
    } finally {
      setRuleSaving(false)
    }
  }

  async function handleDeleteRule(ruleId: string) {
    if (!confirm('Delete this reward rule?')) return
    setDeleting(ruleId)
    try {
      await authApi.delete(`/api/v1/admin/referral/campaigns/${campaignId}/rules/${ruleId}`)
      queryClient.invalidateQueries({ queryKey: ['admin-referral-campaign', campaignId] })
    } catch (e) {
      alert(String(e))
    } finally {
      setDeleting(null)
    }
  }

  async function handleCreateCode() {
    if (!codeEmail.trim()) return
    setCodeSaving(true)
    setCodeError(null)
    setCodeResult(null)
    try {
      const res = await authApi.post<{ code: string; referral_url: string; user_name: string }>(
        `/api/v1/admin/referral/campaigns/${campaignId}/codes`,
        { email: codeEmail.trim(), code: codeCustom.trim() || undefined }
      )
      setCodeResult(res)
      setCodeEmail('')
      setCodeCustom('')
      queryClient.invalidateQueries({ queryKey: ['admin-referral-codes', campaignId] })
    } catch (e) {
      setCodeError(String(e))
    } finally {
      setCodeSaving(false)
    }
  }

  const referralColumns: Column<Referral>[] = [
    {
      id: 'referrer',
      header: 'Referrer',
      cell: (row) => (
        <div>
          <PiiField value={row.referrer_name} presenting={presenting} className="font-medium text-sm" />
          <PiiField value={row.referrer_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
        </div>
      ),
    },
    {
      id: 'referred',
      header: 'Referred',
      cell: (row) => (
        <div>
          <PiiField value={row.referred_name} presenting={presenting} className="font-medium text-sm" />
          <PiiField value={row.referred_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <Badge variant={referralStatusVariant(row.status)}>{row.status}</Badge>,
    },
    {
      id: 'referred_mrr',
      header: 'MRR',
      cell: (row) => row.referred_mrr ? <span className="tabular-nums">{formatCents(row.referred_mrr)}</span> : <span className="text-[var(--content-tertiary)]">--</span>,
      className: 'text-right',
    },
    {
      id: 'created_at',
      header: 'Referred At',
      cell: (row) => <span className="text-[var(--content-secondary)] tabular-nums">{formatTs(row.created_at)}</span>,
    },
    {
      id: 'converted_at',
      header: 'Converted',
      cell: (row) => <span className="text-[var(--content-secondary)] tabular-nums">{formatTs(row.converted_at)}</span>,
    },
  ]

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'rules', label: 'Reward Rules', count: rules.length },
    { id: 'codes', label: 'Codes', count: codesData?.codes?.length },
    { id: 'referrals', label: 'Referrals', count: stats?.total_referrals },
  ]

  return (
    <div>
      <Link to="/referrals/campaigns" className="text-sm text-[var(--brand)] hover:underline mb-4 inline-block">
        &larr; Back to Campaigns
      </Link>

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-heading font-semibold">{campaign.name}</h1>
        <Badge variant={campaignStatusVariant(campaign.status)}>{campaign.status}</Badge>
        {campaign.is_default ? (
          <Badge variant="brand">Default</Badge>
        ) : (
          <button
            onClick={async () => {
              await authApi.put(`/api/v1/admin/referral/campaigns/${campaignId}`, { is_default: true })
              queryClient.invalidateQueries({ queryKey: ['admin-referral-campaign', campaignId] })
              queryClient.invalidateQueries({ queryKey: ['admin-referral-campaigns'] })
            }}
            className="px-3 py-1 text-xs rounded-full border border-[var(--border)] text-[var(--content-secondary)] hover:bg-[var(--surface-nested)] transition-colors cursor-pointer"
          >
            Set as Default
          </button>
        )}
      </div>
      {campaign.description && (
        <p className="text-sm text-[var(--content-secondary)] mb-4">{campaign.description}</p>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <KpiCard label="Total Referrals" value={stats?.total_referrals?.toLocaleString() ?? '0'} />
        <KpiCard label="Conversions" value={stats?.conversions?.toLocaleString() ?? '0'} />
        <KpiCard label="Churned" value={stats?.churned?.toLocaleString() ?? '0'} />
      </div>

      {/* Tabs */}
      <Card>
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        <CardContent>
          {activeTab === 'details' && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  onClick={openEditModal}
                  className="px-4 py-1.5 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] transition-colors cursor-pointer"
                >
                  Edit Campaign
                </button>
              </div>
              <Row label="Referrer Type" value={campaign.referrer_type} />
              <Row label="Referred Product" value={campaign.referred_product} />
              <Row label="Attribution Window" value={`${campaign.attribution_window_days} days`} />
              <Row label="Max Referrals Per User" value={campaign.max_referrals_per_user?.toString() ?? 'Unlimited'} />
              <Row label="Max Total Referrals" value={campaign.max_total_referrals?.toString() ?? 'Unlimited'} />
              <Row label="Pending Days" value={campaign.pending_days?.toString() ?? '0 (instant)'} />
              <Row label="Min Payout" value={campaign.min_payout_cents ? formatCents(campaign.min_payout_cents) : '$10.00 (default)'} />
              <Row label="Starts At" value={formatTs(campaign.starts_at)} />
              <Row label="Ends At" value={formatTs(campaign.ends_at)} />
              <Row label="Created" value={formatTs(campaign.created_at)} />
              <Row label="Total Cost" value={formatCents(campaign.total_cost_cents)} />
            </div>
          )}

          {activeTab === 'rules' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => { setRuleError(null); setRuleOpen(true) }}
                  className="px-4 py-1.5 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] transition-colors cursor-pointer"
                >
                  Add Rule
                </button>
              </div>

              {rules.length === 0 ? (
                <p className="text-sm text-[var(--content-tertiary)] text-center py-6">
                  No reward rules yet. Add one to start rewarding referrals.
                </p>
              ) : (
                rules.map((rule) => (
                  <Card key={rule.id} className="bg-[var(--surface-nested)]">
                    <CardContent className="py-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">
                            On <Badge variant="brand">{rule.trigger_event}</Badge>
                          </p>
                          <p className="text-sm text-[var(--content-secondary)]">
                            Referrer gets: <strong>{rule.referrer_reward_value}</strong> ({rule.referrer_reward_type})
                            {rule.referrer_reward_recurring ? ' — recurring' : ''}
                          </p>
                          {rule.referred_reward_type && (
                            <p className="text-sm text-[var(--content-secondary)]">
                              Referred gets: <strong>{rule.referred_reward_value}</strong> ({rule.referred_reward_type})
                            </p>
                          )}
                          {rule.max_commission_payments && (
                            <p className="text-xs text-[var(--content-tertiary)]">
                              Max commissions: {rule.max_commission_payments}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteRule(rule.id)}
                          disabled={deleting === rule.id}
                          className="px-3 py-1 text-xs rounded-full bg-red-600/10 text-red-600 hover:bg-red-600/20 disabled:opacity-50 transition-colors cursor-pointer"
                        >
                          {deleting === rule.id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {activeTab === 'codes' && (
            <div className="space-y-4">
              {/* Create code form */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-[var(--content-tertiary)] mb-1">User Email</label>
                  <input
                    type="email"
                    value={codeEmail}
                    onChange={(e) => setCodeEmail(e.target.value)}
                    placeholder="referrer@example.com"
                    className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                  />
                </div>
                <div className="w-40">
                  <label className="block text-xs text-[var(--content-tertiary)] mb-1">Custom Code (optional)</label>
                  <input
                    type="text"
                    value={codeCustom}
                    onChange={(e) => setCodeCustom(e.target.value.toUpperCase())}
                    placeholder="DREW2026"
                    maxLength={20}
                    className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
                  />
                </div>
                <button
                  onClick={handleCreateCode}
                  disabled={codeSaving || !codeEmail.trim()}
                  className="px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {codeSaving ? 'Creating...' : 'Create Code'}
                </button>
              </div>

              {codeError && (
                <p className="text-xs text-red-600 dark:text-red-400">{codeError}</p>
              )}

              {codeResult && (
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-sm">
                  <p className="font-medium text-emerald-700 dark:text-emerald-300">
                    Code created for {codeResult.user_name}
                  </p>
                  <p className="mt-1 text-emerald-600 dark:text-emerald-400 font-mono text-xs select-all">
                    {codeResult.referral_url}
                  </p>
                </div>
              )}

              {/* Codes list */}
              {codesLoading ? (
                <div className="flex justify-center py-6"><LoadingSpinner /></div>
              ) : (codesData?.codes?.length ?? 0) === 0 ? (
                <p className="text-sm text-[var(--content-tertiary)] text-center py-6">
                  No referral codes yet. Create one above.
                </p>
              ) : (
                <div className="divide-y divide-[var(--border-subtle)]">
                  {codesData!.codes.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-3">
                      <div>
                        <PiiField value={c.user_name || 'Unknown'} presenting={presenting} className="font-medium text-sm" />
                        <PiiField value={c.user_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
                      </div>
                      <div className="text-right">
                        <span className="font-mono text-sm font-medium">{c.code}</span>
                        <p className="text-xs text-[var(--content-tertiary)]">
                          {c.referral_count} referral{c.referral_count !== 1 ? 's' : ''}
                          {!c.is_active && <Badge variant="warning" className="ml-2">Inactive</Badge>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'referrals' && (
            <DataTable
              columns={referralColumns}
              data={referralsData?.referrals ?? []}
              keyExtractor={(row) => row.id}
              loading={referralsLoading}
              emptyMessage="No referrals for this campaign"
            />
          )}
        </CardContent>
      </Card>

      {/* Edit Campaign Modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Campaign">
        <div className="space-y-3">
          <Field label="Name">
            <input
              type="text"
              value={editForm.name ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
            />
          </Field>
          <Field label="Status">
            <select
              value={editForm.status ?? 'draft'}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value as Campaign['status'] }))}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="ended">Ended</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Referrer Type">
              <select
                value={editForm.referrer_type ?? 'any'}
                onChange={(e) => setEditForm((f) => ({ ...f, referrer_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
              >
                <option value="any">Any</option>
                <option value="student">Student</option>
                <option value="teacher">Teacher</option>
                <option value="studio_owner">Studio Owner</option>
              </select>
            </Field>
            <Field label="Referred Product">
              <select
                value={editForm.referred_product ?? 'academy'}
                onChange={(e) => setEditForm((f) => ({ ...f, referred_product: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
              >
                <option value="academy">Academy</option>
                <option value="studio">Studio</option>
                <option value="teacher">Teacher</option>
                <option value="any">Any</option>
              </select>
            </Field>
          </div>
          <Field label="Attribution Window (days)">
            <input
              type="number"
              value={editForm.attribution_window_days ?? 30}
              onChange={(e) => setEditForm((f) => ({ ...f, attribution_window_days: Number(e.target.value) }))}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
            />
          </Field>
          {editError && (
            <p className="text-xs text-red-600 dark:text-red-400">{editError}</p>
          )}
          <button
            onClick={handleSaveEdit}
            disabled={editSaving}
            className="w-full px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {editSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </Modal>

      {/* Add Rule Modal */}
      <Modal open={ruleOpen} onClose={() => setRuleOpen(false)} title="Add Reward Rule">
        <div className="space-y-3">
          <Field label="Trigger Event">
            <select
              value={ruleForm.trigger_event}
              onChange={(e) => setRuleForm((f) => ({ ...f, trigger_event: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
            >
              {TRIGGER_EVENTS.map((ev) => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Referrer Reward Type">
              <select
                value={ruleForm.referrer_reward_type}
                onChange={(e) => setRuleForm((f) => ({ ...f, referrer_reward_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
              >
                {REFERRER_REWARD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Referrer Reward Value">
              <input
                type="number"
                value={ruleForm.referrer_reward_value}
                onChange={(e) => setRuleForm((f) => ({ ...f, referrer_reward_value: Number(e.target.value) }))}
                placeholder="Amount in cents or units"
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--content-secondary)] cursor-pointer">
            <input
              type="checkbox"
              checked={ruleForm.referrer_reward_recurring === 1}
              onChange={(e) => setRuleForm((f) => ({ ...f, referrer_reward_recurring: e.target.checked ? 1 : 0 }))}
              className="rounded"
            />
            Recurring (monthly commission)
          </label>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Referred Reward Type (optional)">
              <select
                value={ruleForm.referred_reward_type ?? ''}
                onChange={(e) => setRuleForm((f) => ({ ...f, referred_reward_type: e.target.value || null }))}
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
              >
                <option value="">None</option>
                {REFERRED_REWARD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Referred Reward Value">
              <input
                type="number"
                value={ruleForm.referred_reward_value ?? ''}
                onChange={(e) => setRuleForm((f) => ({ ...f, referred_reward_value: e.target.value ? Number(e.target.value) : null }))}
                disabled={!ruleForm.referred_reward_type}
                placeholder="Amount"
                className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none disabled:opacity-50 focus:ring-2 focus:ring-[var(--brand)]/30"
              />
            </Field>
          </div>

          {ruleError && (
            <p className="text-xs text-red-600 dark:text-red-400">{ruleError}</p>
          )}
          <button
            onClick={handleAddRule}
            disabled={ruleSaving}
            className="w-full px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {ruleSaving ? 'Adding...' : 'Add Rule'}
          </button>
        </div>
      </Modal>
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)]">
      <span className="text-sm text-[var(--content-secondary)]">{label}</span>
      <span className="text-sm font-medium capitalize">{value}</span>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-[var(--content-tertiary)] mb-1">{label}</label>
      {children}
    </div>
  )
}
